import React, { useEffect, useRef, useState } from 'react';

import type {
  CodigoRegime,
  IMesDoCalendario,
  Situacao,
} from '../../../../../hooks/useFechamentoContabilMensal';
import {
  alternar,
  chaveDaSelecao,
  MARCOS_DO_MES,
  NOMES_MES,
  numero,
  porcento,
  REGIMES,
  SITUACOES,
} from '../regrasDaCarteira';
import { Anterior, Caixa, Check, Funil, Proximo, SetaBaixo } from './icones';

const rgba = (hex: string, a: number) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(
    hex.slice(5, 7),
    16,
  )}, ${a})`;

const SEM_VINCULO = (rotulo: string) =>
  `Em breve: a ${rotulo.toLowerCase()} ainda não tem vínculo com a empresa no cadastro`;

interface IFiltroTopo {
  chave: string;
  rotulo: string;
  todas: string;
  plural: string;
  /** sem dado no cadastro: fica na tela, desativado */
  semDado: boolean;
  opcoes: { valor: string; nome: string; qtd?: number }[];
  marcados: string[];
  onMudar: (lista: string[]) => void;
}

function FiltroTopo({
  f,
  aberto,
  onAbrir,
  onFechar,
}: {
  f: IFiltroTopo;
  aberto: boolean;
  onAbrir: () => void;
  onFechar: () => void;
}) {
  const nomeDe = (v: string) => f.opcoes.find(o => o.valor === v)?.nome ?? v;
  let valor = f.todas;
  if (f.marcados.length === 1) valor = nomeDe(f.marcados[0]);
  else if (f.marcados.length > 1) valor = `${f.marcados.length} ${f.plural}`;
  return (
    <div className="ftWrap">
      <button
        type="button"
        className={`ftBtn${f.marcados.length ? ' on' : ''}${aberto ? ' aberto' : ''}`}
        onClick={() => (aberto ? onFechar() : onAbrir())}
        aria-expanded={aberto}
        aria-haspopup="listbox"
        disabled={f.semDado}
        title={f.semDado ? SEM_VINCULO(f.rotulo) : undefined}
      >
        <span className="ftRot">{f.rotulo}</span>
        <span className="ftVal">{valor}</span>
        <SetaBaixo tam={12} traco={2.6} className="ftSeta" />
      </button>
      {aberto && !f.semDado && (
        <div className="ftPop" role="listbox" aria-multiselectable="true" aria-label={f.rotulo}>
          {f.opcoes.map(o => {
            const ativo = f.marcados.includes(o.valor);
            return (
              <button
                type="button"
                key={o.valor}
                className="ftOp"
                role="option"
                aria-selected={ativo}
                onClick={() =>
                  f.onMudar(
                    ativo ? f.marcados.filter(x => x !== o.valor) : [...f.marcados, o.valor],
                  )
                }
              >
                <span className={`ftCheck${ativo ? ' on' : ''}`}>
                  <Check tam={10} cor="#FFFFFF" traco={3.6} />
                </span>
                <span className="ftNome">{o.nome}</span>
                <span className="ftQtd">{o.qtd != null ? numero(o.qtd) : ''}</span>
              </button>
            );
          })}
          <div className="ftRodape">
            <button type="button" onClick={() => f.onMudar([])}>
              Limpar
            </button>
            <button type="button" onClick={onFechar}>
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ICalendarioProps {
  ano: number;
  semAnoAnterior: boolean;
  semAnoProximo: boolean;
  onAno: (dir: -1 | 1) => void;
  meses: IMesDoCalendario[];
  hoje: Date;
  sel: string[];
  onSel: (sel: string[]) => void;
  inatSel: string[];
  onInat: (competencia: string) => void;
  regimes: CodigoRegime[];
  porRegime: Record<string, number>;
  onRegimes: (r: CodigoRegime[]) => void;
}

export function Calendario({
  ano,
  semAnoAnterior,
  semAnoProximo,
  onAno,
  meses,
  hoje,
  sel,
  onSel,
  inatSel,
  onInat,
  regimes,
  porRegime,
  onRegimes,
}: ICalendarioProps) {
  const [aberto, setAberto] = useState('');
  const topo = useRef<HTMLDivElement>(null);

  // lista de filtro aberta: fecha ao clicar fora dela ou com Esc
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (ev: PointerEvent) => {
      const alvo = ev.target as HTMLElement | null;
      if (!alvo?.closest?.('.ftWrap')) setAberto('');
    };
    const tecla = (ev: KeyboardEvent) => ev.key === 'Escape' && setAberto('');
    document.addEventListener('pointerdown', fora, true);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('pointerdown', fora, true);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);

  const filtros: IFiltroTopo[] = [
    {
      chave: 'uni',
      rotulo: 'Unidade',
      todas: 'Todas',
      plural: 'selecionadas',
      semDado: true,
      opcoes: [],
      marcados: [],
      onMudar: () => undefined,
    },
    {
      chave: 'reg',
      rotulo: 'Regime',
      todas: 'Todos',
      plural: 'selecionados',
      semDado: false,
      opcoes: REGIMES.map(r => ({ valor: r.codigo, nome: r.nome, qtd: porRegime[r.nome] ?? 0 })),
      marcados: regimes,
      onMudar: lista => onRegimes(lista as CodigoRegime[]),
    },
    {
      chave: 'cart',
      rotulo: 'Carteira',
      todas: 'Todas',
      plural: 'selecionadas',
      semDado: true,
      opcoes: [],
      marcados: [],
      onMudar: () => undefined,
    },
  ];
  const resumoTopo = regimes.map(c => REGIMES.find(r => r.codigo === c)?.nome ?? c).join(', ');
  const porCompetencia = Object.fromEntries(meses.map(m => [m.competencia, m]));

  return (
    <div className="calPilha">
      <span aria-hidden="true" className="folhaAtras f3" />
      <span aria-hidden="true" className="folhaAtras f2" />
      <span aria-hidden="true" className="folhaAtras f1" />
      <section
        aria-label="Calendário de competências"
        style={{
          position: 'relative',
          marginTop: 10,
          background: '#FFFFFF',
          border: '1px solid #D4DAE2',
          borderRadius: 16,
        }}
      >
        <div
          aria-hidden="true"
          className="argolas"
          style={{
            position: 'absolute',
            top: -11,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-evenly',
            pointerEvents: 'none',
          }}
        >
          {Array.from({ length: 10 }, (_, i) => (
            <span
              key={i}
              style={{
                width: 9,
                height: 24,
                borderRadius: 5,
                background: '#475569',
                boxShadow: 'inset -2px 0 0 #334155',
              }}
            />
          ))}
        </div>
        <div className="folha">
          <div
            className="calTopo"
            ref={topo}
            style={{
              padding: '24px 24px 18px',
              borderRadius: '15px 15px 0 0',
              background: '#155FB0',
              color: '#FFFFFF',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                Calendário de competências
              </h2>
              <span style={{ fontSize: 12, color: '#D6E7FB' }}>
                Clique em meses ou situações para selecionar — dá para marcar vários
              </span>
              {regimes.length > 0 && (
                <span
                  style={{
                    fontSize: 12,
                    color: '#FFFFFF',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Funil tam={12} cor="#FFFFFF" traco={2.4} />
                  <b style={{ fontWeight: 600 }}>{resumoTopo}</b>
                  <button
                    type="button"
                    onClick={() => onRegimes([])}
                    style={{
                      border: 0,
                      background: 'transparent',
                      padding: 0,
                      color: '#D6E7FB',
                      fontSize: 12,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    limpar
                  </button>
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifySelf: 'center' }}>
              {(
                [
                  [-1, 'Ano anterior', semAnoAnterior],
                  [1, 'Próximo ano', semAnoProximo],
                ] as const
              ).map(([dir, rotulo, sem], i) => (
                <React.Fragment key={dir}>
                  {i === 1 && (
                    <span
                      style={{
                        fontSize: 36,
                        fontWeight: 700,
                        letterSpacing: '-.01em',
                        lineHeight: 1,
                        minWidth: 100,
                        textAlign: 'center',
                      }}
                    >
                      {ano}
                    </span>
                  )}
                  <button
                    type="button"
                    className="anoBtn"
                    aria-label={rotulo}
                    onClick={() => onAno(dir)}
                    disabled={sem}
                    style={{
                      width: 36,
                      height: 36,
                      border: '1px solid rgba(255, 255, 255, .45)',
                      background: 'transparent',
                      borderRadius: 999,
                      cursor: sem ? 'default' : 'pointer',
                      opacity: sem ? 0.4 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {dir < 0 ? <Anterior cor="#FFFFFF" /> : <Proximo cor="#FFFFFF" />}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div
              style={{
                justifySelf: 'end',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minWidth: 0,
              }}
            >
              <div className="topoFiltros">
                {filtros.map(f => (
                  <FiltroTopo
                    key={f.chave}
                    f={f}
                    aberto={aberto === f.chave}
                    onAbrir={() => setAberto(f.chave)}
                    onFechar={() => setAberto('')}
                  />
                ))}
              </div>
              <div
                className="marca"
                role="img"
                aria-label="Especialistas — Inteligência Contábil"
                title="Especialistas — Inteligência Contábil"
              >
                <img className="marcaImg" src="/logo-especialistas.png" alt="" />
              </div>
            </div>
          </div>
          <div className="cal" style={{ padding: 20 }}>
            {NOMES_MES.map((nome, i) => {
              const competencia = `${ano}-${String(i + 1).padStart(2, '0')}`;
              return (
                <CartaoDoMes
                  key={competencia}
                  nome={nome}
                  indice={i}
                  ano={ano}
                  competencia={competencia}
                  dado={porCompetencia[competencia]}
                  hoje={hoje}
                  sel={sel}
                  onSel={onSel}
                  inatAtivo={inatSel.includes(competencia)}
                  onInat={() => onInat(competencia)}
                />
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

const opacidadeDoDesenho = (disp: boolean, selecionado: boolean): number => {
  if (!disp) return 0.35;
  return selecionado ? 0.8 : 0.55;
};

function CartaoDoMes({
  nome,
  indice,
  ano,
  competencia,
  dado,
  hoje,
  sel,
  onSel,
  inatAtivo,
  onInat,
}: {
  nome: string;
  indice: number;
  ano: number;
  competencia: string;
  dado: IMesDoCalendario | undefined;
  hoje: Date;
  sel: string[];
  onSel: (sel: string[]) => void;
  inatAtivo: boolean;
  onInat: () => void;
}) {
  const marco = MARCOS_DO_MES[indice];
  const atual = ano === hoje.getFullYear() && indice === hoje.getMonth();
  // o mês corrente não é revisado (16/10/2026), mesmo que já exista apuração dele
  const disp = !!dado && dado.total > 0 && !atual;
  const chaves = SITUACOES.map(s => chaveDaSelecao(competencia, s.chave));
  const selecionado = chaves.some(k => sel.includes(k));
  const desenho = (
    <span
      className="desenho desenhoDica"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label={`${marco.titulo}: ${marco.texto}`}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 64 64"
        fill="none"
        stroke={disp ? '#1B6FD0' : '#64748B'}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: opacidadeDoDesenho(disp, selecionado) }}
      >
        <path d={marco.desenho} />
      </svg>
      <span className={`dicaBalao${indice >= 9 ? ' cima' : ''}`} role="tooltip" aria-hidden="true">
        <b>{marco.titulo}</b>
        {marco.texto}
      </span>
    </span>
  );

  if (!disp) {
    const futuro =
      ano > hoje.getFullYear() || (ano === hoje.getFullYear() && indice > hoje.getMonth());
    let etiqueta = 'sem revisão';
    let nota = 'nenhuma competência revisada neste mês';
    if (atual) {
      etiqueta = 'mês atual';
      nota = 'competência em curso · a revisão começa no mês seguinte';
    } else if (futuro) {
      etiqueta = 'não iniciada';
      nota = 'competência ainda não iniciada';
    }
    return (
      <div className="mesSlot">
        <div
          style={{
            minWidth: 0,
            boxSizing: 'border-box',
            border: '1px dashed #CBD5E1',
            borderRadius: 12,
            background: '#F8FAFC',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {desenho}
          <button
            type="button"
            disabled
            aria-label={`${nome} de ${ano}: ${nota}`}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              textAlign: 'left',
              cursor: 'default',
              padding: '16px 20px 12px',
              border: 0,
              borderRadius: 12,
              background: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              color: '#0F172A',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 700, paddingTop: 1, color: '#52525B' }}>
                {nome}
              </span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 700,
                  background: atual ? '#E9F4FD' : '#EEF2F6',
                  color: atual ? '#155FB0' : '#52525B',
                }}
              >
                {etiqueta}
              </span>
            </span>
            <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: '#64748B' }}>
              —
            </span>
            <span style={{ fontSize: 11.5, color: '#52525B' }}>{nota}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mesSlot">
      <div
        className={`mesCard${selecionado ? ' sel' : ''}`}
        style={{
          minWidth: 0,
          boxSizing: 'border-box',
          border: `1px solid ${selecionado ? '#1B6FD0' : '#E4E4E7'}`,
          borderRadius: 12,
          background: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {desenho}
        <div className="mesTotais">
          <span className="mtLinha">
            <span className="mtRot">Total</span>
            <span className="mtNum">{numero(dado.total)}</span>
            <span className="mtRot">Empresas</span>
          </span>
          <button
            type="button"
            className={`mtLinha mtInat${inatAtivo ? ' on' : ''}`}
            onClick={onInat}
            aria-pressed={inatAtivo}
            aria-label={`${inatAtivo ? 'Tirar da carteira as' : 'Ver na carteira as'} ${numero(
              dado.inativas,
            )} empresas inativas de ${nome}`}
            title="Inativas no cadastro ou fora do período em que são clientes do escritório (cliente desde / até). Não entram no total · clique para ver na carteira"
          >
            <span className="mtRot">Inativas</span>
            <span className="mtNum">{numero(dado.inativas)}</span>
            <span className="mtRot">Empresas</span>
          </button>
        </div>
        <button
          type="button"
          onClick={() => onSel(alternar(sel, chaves))}
          aria-pressed={selecionado}
          aria-label={`${nome} de ${ano}: ${numero(dado.total)} competências`}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            textAlign: 'left',
            cursor: 'pointer',
            padding: '16px 20px 12px',
            border: 0,
            borderRadius: 12,
            background: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            color: '#0F172A',
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                paddingTop: 1,
                color: selecionado ? '#155FB0' : '#0F172A',
              }}
            >
              {nome}
            </span>
          </span>
        </button>
        <div style={{ padding: '0 20px 16px' }}>
          <div
            style={{
              borderTop: '1px solid #E4E4E7',
              paddingTop: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {SITUACOES.map(s => {
              const g = dado.situacoes[s.chave as Situacao];
              const chave = chaveDaSelecao(competencia, s.chave);
              const ativo = sel.includes(chave);
              return (
                <button
                  type="button"
                  key={s.chave}
                  className="grp"
                  onClick={() => onSel(alternar(sel, [chave]))}
                  aria-pressed={ativo}
                  aria-label={`${s.nome} em ${nome}: ${numero(g.qtd)} empresas — ${
                    ativo ? 'desmarcar' : 'selecionar'
                  }`}
                  style={{
                    width: 'calc(100% + 16px)',
                    margin: '0 -8px',
                    boxSizing: 'border-box',
                    padding: '6px 8px',
                    border: 0,
                    borderRadius: 8,
                    background: ativo ? rgba(s.cor, 0.08) : 'transparent',
                    boxShadow: ativo ? `inset 0 0 0 1px ${rgba(s.cor, 0.32)}` : 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#0F172A',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        flexShrink: 0,
                        background: s.cor,
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.nome}
                    </span>
                    <span style={{ fontWeight: 700 }}>{numero(g.qtd)}</span>
                    <span
                      style={{
                        minWidth: 40,
                        padding: '1px 0',
                        borderRadius: 6,
                        textAlign: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        background: s.suave,
                        color: s.texto,
                      }}
                    >
                      {porcento(g.qtd, dado.total)}
                    </span>
                    <Caixa ativo={ativo} cor={s.cor} />
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '2px 14px',
                      paddingLeft: 16,
                      fontSize: 11.5,
                      color: '#52525B',
                    }}
                  >
                    {s.subs.map(sb => {
                      const q = g.subs[sb.chave] ?? 0;
                      return (
                        <span
                          key={sb.chave}
                          style={{ whiteSpace: 'nowrap', opacity: sb.semDado ? 0.55 : 1 }}
                          title={sb.dica}
                        >
                          {sb.rotulo}{' '}
                          <b style={{ fontWeight: 700, color: '#0F172A' }}>{numero(q)}</b> ·{' '}
                          {porcento(q, g.qtd)}
                        </span>
                      );
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
